import React, { Component, memo } from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import moment from '~/Providers/Moment';

import { DAY_WEEK_EVENT } from '~/Config/DragTypes';
import { DATE_TIME_FORMAT } from '~/Redux/Constants';
import { openPresetModal, updateEvent } from '~/Redux/Modules/Events';
import { addUndo } from '~/Redux/Modules/Undo';
import { findCalendarInList, mapCalendarsToList } from '~/Redux/Modules/CalendarAccounts';
import { findUserTimeZone } from '~/Context/Event';

import './TimeSlot.scss';

export const eventTarget = {
  canDrop: ({ start }, monitor) => {
    const previous = monitor.getItem().event;
    const userTimeZone = findUserTimeZone();
    return (
      moment
        .tz(
          previous.start,
          DATE_TIME_FORMAT,
          (previous.timezone && previous.timezone.name) || userTimeZone
        )
        .tz(userTimeZone)
        .format(DATE_TIME_FORMAT) !== start.format(DATE_TIME_FORMAT)
    );
  },
  drop: ({ updateEvent, addUndo, start, currentView, currentViewStart }, monitor) => {
    const event = monitor.getItem().event;
    const userTimeZone = findUserTimeZone();
    let newStart = moment
      .tz(start, DATE_TIME_FORMAT, userTimeZone)
      .tz((event.timezone && event.timezone.name) || userTimeZone);
    let newEnd = newStart
      .clone()
      .add(moment(event.end, DATE_TIME_FORMAT).diff(moment(event.start, DATE_TIME_FORMAT)));
    updateEvent(
      {
        ...event,
        start: newStart.format(DATE_TIME_FORMAT),
        end: newEnd.format(DATE_TIME_FORMAT),
        confirm: 'this_event',
      },
      currentView,
      currentViewStart
    ).then(() => {
      addUndo({
        title: 'Event dragged to new time',
        callback: () => {
          updateEvent({ ...event, confirm: 'this_event' }, currentView, currentViewStart);
        },
      });
    });
  },
};

const dropCollect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
});

@DropTarget(DAY_WEEK_EVENT, eventTarget, dropCollect)
export class TimeSlot extends Component {
  handleMakeHourEvent = () => {
    const { openPresetModal, start, end, selecting } = this.props;
    if (selecting) return;
    openPresetModal({
      start: start.format(DATE_TIME_FORMAT),
      end: end.format(DATE_TIME_FORMAT),
      color: this.props.defaultCalendar.color,
    });
  };

  render() {
    const { showLabel, content, connectDropTarget, isOver, start } = this.props;
    const className = isOver ? 'drop-is-over' : '';
    let id = '';
    if (
      start.hour() === moment().hour() &&
      ((start.minutes() < 30 && moment().minutes() < 30) ||
        (start.minutes() >= 30 && moment().minutes() >= 30))
    ) {
      id = 'currentTime';
    }
    return connectDropTarget(
      <div id={id} className={`timeslot-division ${className}`} onClick={this.handleMakeHourEvent}>
        {showLabel ? content : ''}
      </div>
    );
  }
}

TimeSlot.propTypes = {
  showLabel: propTypes.bool,
  isOver: propTypes.bool,
  content: propTypes.oneOfType([propTypes.string, propTypes.number]),
  openPresetModal: propTypes.func,
  connectDropTarget: propTypes.func,
  start: propTypes.object,
  end: propTypes.object,
  selecting: propTypes.bool,
  defaultCalendar: propTypes.object,
};

TimeSlot.defaultProps = {
  showLabel: false,
};

const mapStateToProps = ({ UserOptions, CalendarAccounts, DateRange }) => {
  return {
    defaultCalendar:
      findCalendarInList(mapCalendarsToList(CalendarAccounts), UserOptions.data.calendar_id) || {},
    currentView: DateRange.currentType,
    currentViewStart: DateRange.start,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    openPresetModal: update => dispatch(openPresetModal(update)),
    updateEvent: (data, currentView, currentViewStart) =>
      dispatch(updateEvent(data, currentView, currentViewStart.clone())),
    addUndo: undo => dispatch(addUndo(undo)),
  };
};
export default memo(
  TimeSlot |> connect(mapStateToProps, mapDispatchToProps),
  ({ day, start, end, ...rest }, nextProps) => {
    if (!day.isSame(nextProps.day, 'day')) return false;
    if (!start.isSame(nextProps.start, 'day')) return false;
    if (!end.isSame(nextProps.end, 'day')) return false;
    return Object.keys(rest)
      .map(key => rest[key] === nextProps[key])
      .reduce((a, e) => a && e, true);
  }
);
